import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Crown, User, Link2, Trash2, Copy, Check, ArrowLeft, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useToast } from '../components/ui/toast';
import Layout from '../components/Layout';
import { meetingAPI, MeetingParticipantsResponse, MeetingParticipantWithJoinLink, AddParticipantRequest } from '../services/api';

const MeetingParticipants: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [data, setData] = useState<MeetingParticipantsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newParticipantName, setNewParticipantName] = useState('');
  const [newParticipantIsHost, setNewParticipantIsHost] = useState(false);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [copiedLinks, setCopiedLinks] = useState<Set<string>>(new Set());

  const fetchParticipants = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await meetingAPI.getMeetingParticipants(id);
      setData(response.data.data);
    } catch (error: any) {
      console.error('Error fetching participants:', error);
      addToast({ title: error.response?.data?.message || 'Failed to fetch participants', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipant = async () => {
    if (!id || !newParticipantName.trim()) return;

    try {
      setAddingParticipant(true);
      const requestData: AddParticipantRequest = {
        user_name: newParticipantName.trim(),
        is_host: newParticipantIsHost,
      };
      
      await meetingAPI.addParticipant(id, requestData);
      addToast({ title: 'Participant added successfully', variant: 'success' });
      
      // Reset form
      setNewParticipantName('');
      setNewParticipantIsHost(false);
      setAddDialogOpen(false);
      
      // Refresh participants list
      await fetchParticipants();
    } catch (error: any) {
      console.error('Error adding participant:', error);
      addToast({ title: error.response?.data?.message || 'Failed to add participant', variant: 'destructive' });
    } finally {
      setAddingParticipant(false);
    }
  };

  const handleMakeHost = async (participantId: string) => {
    if (!id) return;

    try {
      await meetingAPI.updateParticipant(id, participantId, { is_host: true });
      addToast({ title: 'Participant promoted to host', variant: 'success' });
      await fetchParticipants();
    } catch (error: any) {
      console.error('Error updating participant:', error);
      addToast({ title: error.response?.data?.message || 'Failed to update participant', variant: 'destructive' });
    }
  };

  const handleDeleteParticipant = async (participantId: string, participantName: string) => {
    if (!id) return;

    if (!window.confirm(`Are you sure you want to delete participant "${participantName}"?`)) {
      return;
    }

    try {
      await meetingAPI.deleteParticipant(id, participantId);
      addToast({ title: 'Participant deleted successfully', variant: 'success' });
      await fetchParticipants();
    } catch (error: any) {
      console.error('Error deleting participant:', error);
      addToast({ title: error.response?.data?.message || 'Failed to delete participant', variant: 'destructive' });
    }
  };

  const handleCopyLink = async (link: string, participantId: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLinks(prev => new Set(prev).add(participantId));
      addToast({ title: 'Join link copied to clipboard', variant: 'success' });
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedLinks(prev => {
          const newSet = new Set(prev);
          newSet.delete(participantId);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error('Error copying link:', error);
      addToast({ title: 'Failed to copy link', variant: 'destructive' });
    }
  };

  const getParticipantInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  useEffect(() => {
    fetchParticipants();
  }, [id]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const getParticipantRoleBadge = (isHost: boolean) => {
    return isHost ? (
      <Badge variant="default" className="bg-yellow-100 text-yellow-800">
        <Crown className="w-3 h-3 mr-1" />
        Host
      </Badge>
    ) : (
      <Badge variant="secondary">
        <User className="w-3 h-3 mr-1" />
        Participant
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout title="Meeting Participants">
        <div className="container max-w-6xl mx-auto p-6">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout title="Meeting Participants">
        <div className="container max-w-6xl mx-auto p-6">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Meeting not found</h3>
              <p className="text-muted-foreground text-center">
                The requested meeting could not be found.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Meeting Participants">
      <div className="container max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger 
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                onClick={() => setAddDialogOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Add Participant
              </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Participant</DialogTitle>
                    <DialogDescription>
                      Create a new participant with a unique join link for this meeting.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="participant-name">Participant Name</Label>
                      <Input
                        id="participant-name"
                        placeholder="Enter participant name"
                        value={newParticipantName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewParticipantName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newParticipantName.trim()) {
                            handleAddParticipant();
                          }
                        }}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is-host"
                        checked={newParticipantIsHost}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewParticipantIsHost(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="is-host" className="text-sm">
                        Make this participant the host
                      </Label>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setAddDialogOpen(false);
                        setNewParticipantName('');
                        setNewParticipantIsHost(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAddParticipant}
                      disabled={!newParticipantName.trim() || addingParticipant}
                    >
                      {addingParticipant ? 'Adding...' : 'Add Participant'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        {/* Participants List */}
        {data.participants.length === 0 ? (
          <div className='container max-w-6xl mx-auto p-6'>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No participants found</h3>
              <p className="text-muted-foreground text-center">
                This meeting doesn't have any participants yet.
              </p>
            </CardContent>
          </Card>
          </div>
        ) : (
          <div className="container max-w-6xl mx-auto p-6 space-y-4">
            {data.participants.map((participant: MeetingParticipantWithJoinLink) => (
              <Card key={participant.id} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-sm">
                          {getParticipantInitial(participant.user_name)}
                        </div>
                      </div>
                      <div>
                        <CardTitle className="text-base">
                          {participant.user_name}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {participant.is_host ? "Meeting Host" : "Participant"}
                        </p>
                      </div>
                    </div>
                    {getParticipantRoleBadge(participant.is_host)}
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>Added: {formatDate(participant.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Link2 className="h-4 w-4 text-muted-foreground" />
                      <span className="truncate font-mono text-xs">
                        Token: {participant.token.slice(0, 8)}...
                      </span>
                    </div>
                  </div>

                  <div className="border-t my-4" />

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      Join Link Available
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyLink(participant.join_link, participant.id)}
                        className="flex items-center gap-2"
                      >
                        {copiedLinks.has(participant.id) ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                        {copiedLinks.has(participant.id) ? 'Copied' : 'Copy Link'}
                      </Button>

                      {!participant.is_host && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMakeHost(participant.id)}
                          className="flex items-center gap-2"
                        >
                          <Crown className="h-4 w-4" />
                          Make Host
                        </Button>
                      )}

                      { (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteParticipant(participant.id, participant.user_name)}
                          className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      
    </Layout>
  );
};

export default MeetingParticipants;
